/**
 * Ширина полосы в выборке — одна формула на витрину и на кабинет.
 *
 * Правило (data-viz.md §3.2): **ширина считается от максимума текущей выборки,
 * а напечатанное рядом число остаётся абсолютным.** Лидер занимает дорожку
 * целиком и несёт модификатор `.top`. Смысл в том, что полосу рисуют ради формы рейтинга:
 * если лидер занимает половину дорожки, вторая половина графика ничего не
 * говорит, а разрывы между участниками сжимаются вдвое.
 *
 * Знаменатель **не меняется** от сортировки и листания. Если максимум остался
 * на первой странице, все полосы на второй короткие — это правда, а не дефект.
 *
 * Файл общий не из экономии в двадцать строк, а потому что до него витрина
 * ставила абсолютный процент, а кабинет — долю от лидера: одни и те же данные
 * рисовались двумя разными способами на двух поверхностях одного продукта.
 */

export interface BarRow {
  /** Значение метрики. `null` — не измерено; это не ноль. */
  value: number | null;
}

export interface BarGeometry {
  /** Готовая строка для `--w`: `50.7%`. Ставится в разметку, не в скрипт. */
  width: string;
  /** Полоса лидера: единственная во всей выборке, если максимум не повторяется. */
  lead: boolean;
  /** Значение измерено. `false` — рисуется штриховка «Не измерено». */
  measured: boolean;
}

/** Максимум выборки. Неизмеренные строки в него не входят. */
export function barMax(rows: readonly BarRow[]): number {
  let max = 0;
  for (const row of rows) {
    if (row.value !== null && row.value > max) max = row.value;
  }
  return max;
}

/**
 * Геометрия одной полосы относительно максимума выборки.
 *
 * Максимум передаётся снаружи, а не считается здесь: он общий на весь блок и
 * не должен пересчитываться на каждой строке — именно так знаменатель и
 * начинает ездить от сортировки.
 */
export function barGeometry(value: number | null, max: number): BarGeometry {
  const measured = value !== null;
  if (!measured || max <= 0) return { width: '0%', lead: false, measured };
  return {
    width: `${((value / max) * 100).toFixed(1)}%`,
    lead: value === max,
    measured: true,
  };
}

/** Геометрия всей выборки разом: максимум считается один раз. */
export function barGeometries(rows: readonly BarRow[]): BarGeometry[] {
  const max = barMax(rows);
  return rows.map((row) => barGeometry(row.value, max));
}
